/* eslint-disable no-use-before-define */
import React from 'react';
import { Button, Chip, Stack } from '@mui/material';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import moment from 'moment';
import {
  getUserMessagesAPI,
  deleteMessageAPI,
  updateMessageAPI,
  getUserRobotsAPI,
  sendMessageAPI,
} from '../api/message';
import {
  getManagerMode,
  getManagerRobotId,
  getManagerRobotSerial,
  getUserPId,
  Log,
} from '../common/util';
import {
  MODAL_TYPE_MESSAGE_DELETE,
  MODAL_TYPE_MESSAGE_TRANSMIT,
  MODAL_TYPE_ROBOT_SEARCH,
} from '../common/consts';
import { deletePiboMsg, sendPiboMsg, setGroup } from '../pibo';
import MessagePlayer from '../components/message/MessagePlayer';

const MODULE_MESSAGE = 'messageBot';

const renameStatus = (status) => {
  switch (status) {
    case 0:
      return '대기중';
    case 1:
      return '완료';
    case 2:
      return '실패';
    default:
      return '알 수 없음';
  }
};
const colorStatus = (status) => {
  switch (status) {
    case 0:
      return 'warning';
    case 1:
      return 'success';
    case 2:
      return 'error';
    default:
      return 'default';
  }
};

const modalInitial = {
  loading: false,
  open: false,
  items: [],
  bDel: false,
  error: null,
};

const stateInitial = { pending: false, result: null, message: null };

const initialState = {
  loading: false,
  list: [],
  contentItems: [],
  listLength: 0,
  total: 0,
  error: null,
  modal: { ...modalInitial },
  robotList: [],
  send: { ...stateInitial },
  transmit: { ...stateInitial },
  delete: { ...stateInitial },
  playItem: null,
};

const createListData = (d, dispatch) => {
  const { id, value: v, serial, mode, type, updatedAt, status } = d;
  const statusChip = (
    <Chip
      size="small"
      color={colorStatus(status)}
      label={renameStatus(status)}
      sx={{ height: 20 }}
    />
  );

  const buttons = (
    <Stack direction="row">
      <Button
        name={MODAL_TYPE_MESSAGE_TRANSMIT}
        color="secondary"
        size="small"
        variant="contained"
        sx={{ minWidth: 50, mr: 1 }}
      >
        재전송
      </Button>
      <Button
        name={MODAL_TYPE_MESSAGE_DELETE}
        color="error"
        size="small"
        variant="contained"
        sx={{ minWidth: 50 }}
      >
        삭제
      </Button>
    </Stack>
  );

  const value =
    type === 'text' ? (
      v
    ) : (
      <MessagePlayer
        id={id}
        value={v}
        setPlayItem={(arg) =>
          dispatch(setPlayItem(arg ? `${arg.id}_${arg.value}` : null))
        }
      />
    );

  const item = [
    { key: 'value', content: value },
    { key: 'serial', content: serial },
    { key: 'mode', content: mode === 'realTime' ? '실시간' : '인체감지' },
    {
      key: 'date',
      content: moment(new Date(updatedAt)).format('YYYY/MM/DD HH:mm:ss'),
    },
    { key: 'status', content: statusChip },
    { key: 'buttons', content: buttons },
  ];

  return { id, key: id, item, disabled: false };
};

// iot 메시지 전달 구문 추가
export const transmitMessage = createAsyncThunk(
  `${MODULE_MESSAGE}/transmit`,
  async (list, { rejectWithValue }) => {
    try {
      const { result } = await updateMessageAPI({ msgs: list });
      if (result) {
        const sendObj = list.reduce((acc, { mode, type, serial }) => {
          const entries = Object.entries(acc);
          const key = `${mode}_${type}`;

          if (entries.length > 0) {
            const [[k, v]] = entries;
            const idx = k.indexOf(key);
            if (idx > -1) {
              return { ...acc, [key]: [...v, serial] };
            }
          }
          return { ...acc, [key]: [serial] };
        }, {});

        Object.entries(sendObj).forEach((el) => {
          const [k, v] = el;
          const [mode, type] = k.split('_');
          setGroup(v);
          sendPiboMsg({ mode, type }, (re) => {
            Log('tranmit message', re);
          });
        });
        return '메시지 재전송이 완료되었습니다.';
      }

      return rejectWithValue('메시지 재전송 도중 오류가 발생했습니다.');
    } catch (error) {
      // console.log(error);
      return rejectWithValue('메시지 재전송 도중 오류가 발생했습니다.');
    }
  },
);

export const deleteMessage = createAsyncThunk(
  `${MODULE_MESSAGE}/delete`,
  async (list, { rejectWithValue }) => {
    try {
      const [msgs, robotIds] = list.reduce(
        (acc, { id, serial }) => [
          [...acc[0], id],
          [...acc[1], serial],
        ],
        [[], []],
      );
      const { result } = await deleteMessageAPI({ msgs });
      if (result) {
        setGroup(robotIds);
        deletePiboMsg(msgs);
        return '메시지 삭제가 완료되었습니다.';
      }
      return rejectWithValue('메시지 삭제 도중 오류가 발생했습니다.');
    } catch (error) {
      return rejectWithValue('메시지 삭제 도중 오류가 발생했습니다.');
    }
  },
);

// iot 메시지 전달 구문 추가
export const sendMessage = createAsyncThunk(
  `${MODULE_MESSAGE}/send`,
  async (conditions, { rejectWithValue }) => {
    try {
      let result;
      let robots;
      if (getManagerMode()) {
        robots = [getManagerRobotSerial()];
        const { result: r } = await sendMessageAPI({
          ...conditions,
          userId: getUserPId(),
          robots: [getManagerRobotId()],
        });
        result = r;
      } else {
        const userId = getUserPId();
        const { robots: robotList } = conditions;
        let rs;
        if (robotList.length === 0) {
          const { result: robotResult, robots: list } = await getUserRobotsAPI({
            userId,
          });
          if (!robotResult)
            return rejectWithValue(
              '메시지 전송을 위한 로봇 정보 로드 중 오류가 발생했습니다.',
            );

          const [i, j] = list.reduce(
            (acc, { id, serial }) => [
              [...acc[0], id],
              [...acc[1], serial],
            ],
            [[], []],
          );
          rs = i;
          robots = j;
        } else {
          const [i, j] = robotList.reduce(
            (acc, { id, serial }) => [
              [...acc[0], id],
              [...acc[1], serial],
            ],
            [[], []],
          );
          rs = i;
          robots = j;
        }
        const { result: r } = await sendMessageAPI({
          ...conditions,
          robots: rs,
          userId: getUserPId(),
        });
        result = r;
      }
      if (result) {
        setGroup(robots);
        const { mode } = conditions;
        sendPiboMsg({ mode, type: 'text' }, (re) => {
          Log('send message', re);
        });
        return '메시지 전송이 완료되었습니다.';
      }
      return rejectWithValue('메시지 전송 도중 오류가 발생했습니다.');
    } catch (error) {
      return rejectWithValue('메시지 전송 도중 오류가 발생했습니다.');
    }
  },
);

export const getData = createAsyncThunk(
  `${MODULE_MESSAGE}/getData`,
  async (rest, { rejectWithValue, dispatch }) => {
    try {
      const conditions = getManagerMode()
        ? { ...rest, robotId: getManagerRobotId() }
        : { ...rest, userId: getUserPId() };
      const { result, list, total } = await getUserMessagesAPI(conditions);
      if (result) {
        const contentItems = list.map((item) => createListData(item, dispatch));
        return { list, contentItems, total, listLength: 0 };
      }
      return rejectWithValue('메시지 목록 로드 중 오류가 발생했습니다.');
    } catch (error) {
      return rejectWithValue('메시지 목록 로드 중 오류가 발생했습니다.');
    }
  },
);

export const openModal = createAsyncThunk(
  `${MODULE_MESSAGE}/openModal`,
  async ({ items, type }, { rejectWithValue }) => {
    if (type !== MODAL_TYPE_ROBOT_SEARCH) {
      return { open: true, bDel: type === MODAL_TYPE_MESSAGE_DELETE, items };
    }
    const userId = getUserPId();
    const { result, robots } = await getUserRobotsAPI({ userId });
    if (result) {
      return {
        open: true,
        bDel: null,
        items: robots,
      };
    }
    return rejectWithValue('로봇 목록 로드 중 오류가 발생했습니다.');
  },
);

const messageSlice = createSlice({
  name: MODULE_MESSAGE,
  initialState,
  reducers: {
    sendInit: (state) => ({ ...state, send: { ...stateInitial } }),
    closeModal: (state) => ({
      ...state,
      modal: { loading: false, open: false, items: [], bDel: false },
    }),
    setPlayItem: (state, action) => ({ ...state, playItem: action.payload }),
  },
  extraReducers: {
    [getData.pending.type]: (state) => ({ ...state, loading: true }),
    [getData.fulfilled.type]: (state, action) => ({
      ...state,
      loading: false,
      ...action.payload,
    }),
    [getData.rejected.type]: (state, action) => ({
      ...state,
      loading: false,
      error: action.payload,
    }),
    [openModal.pending.type]: (state) => ({
      ...state,
      modal: { ...modalInitial, loading: true },
    }),
    [openModal.fulfilled.type]: (state, action) => ({
      ...state,
      modal: { loading: false, ...action.payload },
    }),
    [openModal.rejected.type]: (state, action) => ({
      ...state,
      modal: { loading: false, error: action.payload },
    }),
    [transmitMessage.pending.type]: (state) => ({
      ...state,
      delete: { ...stateInitial },
      send: { ...stateInitial },
      transmit: { ...stateInitial, pending: true },
    }),
    [transmitMessage.fulfilled.type]: (state, action) => ({
      ...state,
      transmit: { pending: false, result: true, message: action.payload },
    }),
    [transmitMessage.rejected.type]: (state, action) => ({
      ...state,
      transmit: { pending: false, result: false, message: action.payload },
    }),
    [deleteMessage.pending.type]: (state) => ({
      ...state,
      transmit: { ...stateInitial },
      send: { ...stateInitial },
      delete: { ...stateInitial, pending: true },
    }),
    [deleteMessage.fulfilled.type]: (state, action) => ({
      ...state,
      delete: { pending: false, result: true, message: action.payload },
    }),
    [deleteMessage.rejected.type]: (state, action) => ({
      ...state,
      delete: { pending: false, result: false, message: action.payload },
    }),
    [sendMessage.pending.type]: (state) => ({
      ...state,
      transmit: { ...stateInitial },
      delete: { ...stateInitial },
      send: { ...stateInitial, pending: true },
    }),
    [sendMessage.fulfilled.type]: (state, action) => ({
      ...state,
      send: { pending: false, result: true, message: action.payload },
    }),
    [sendMessage.rejected.type]: (state, action) => ({
      ...state,
      send: { pending: false, result: false, message: action.payload },
    }),
  },
});

const { reducer: MessageReducer, actions } = messageSlice;
export const { sendInit, closeModal, setPlayItem } = actions;

export default MessageReducer;
